'use client';

import { useMemo } from 'react';
import { MailIcon, PhoneCallIcon } from 'lucide-react';

import { CustomerAdminServiceTypes } from '@/app/(app)/customers/[id]/customer-admin';
import { CustomerAppointments } from '@/app/(app)/customers/[id]/customer-appointments';
import { CustomerBooking } from '@/app/(app)/customers/[id]/customer-booking';
import { CustomerBookingProvider } from '@/app/(app)/customers/[id]/customer-booking-provider';
import CustomerNotes from '@/app/(app)/customers/[id]/customer-notes';
import { CustomerOverview } from '@/app/(app)/customers/[id]/customer-overview';
import { CustomerPreferences } from '@/app/(app)/customers/[id]/customer-preferences';
import { CustomerServicePlans } from '@/app/(app)/customers/[id]/customer-service-plans';
import { CustomerProvider, useCustomerCtx } from '@/app/(app)/customers/[id]/provider';
import { PageHeader } from '@/app/(app)/page-main';
import { SuperUserOnly } from '@/components/providers/admin-only';
import { Button } from '@/components/ui/button';
import { PageTabsList, PageTabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { copyToClipboard } from '@/lib/clipboard';
import { formatPhoneNumber } from '@/lib/format';
import { useLocationParam } from '@/lib/router';

export function CustomerPage() {
  const id = useLocationParam('id');
  return (
    <CustomerProvider id={id}>
      <CustomerBookingProvider>
        <Content />
      </CustomerBookingProvider>
    </CustomerProvider>
  );
}

function Content() {
  const { customer } = useCustomerCtx();

  return (
    <div className='flex h-[calc(100vh-1rem)] w-full flex-row overflow-hidden'>
      <div className='flex w-full flex-col overflow-y-hidden'>
        <PageHeader title={customer.name} breadcrumbs={[{ label: 'Customers', href: '/customers' }]} />
        <div className='flex flex-col gap-1 border-b-[0.5px] px-4 pt-2'>
          <CustomerNameAndActions />
          <PageTabsList>
            <PageTabsTrigger value='overview'>Account</PageTabsTrigger>
            <PageTabsTrigger value='appointments'>Appointments</PageTabsTrigger>
            <PageTabsTrigger value='preferences'>Preferences</PageTabsTrigger>
            <PageTabsTrigger value='notes'>Notes</PageTabsTrigger>
            <PageTabsTrigger value='service-plans'>Subscriptions</PageTabsTrigger>
          </PageTabsList>
        </div>
        <div className='flex w-full flex-col overflow-y-auto px-4 py-6'>
          <TabsContent value='overview'>
            <CustomerOverview />
          </TabsContent>
          <TabsContent value='appointments'>
            <CustomerAppointments />
          </TabsContent>
          <TabsContent value='preferences'>
            <CustomerPreferences />
          </TabsContent>
          <TabsContent value='notes'>
            <CustomerNotes />
          </TabsContent>
          <TabsContent value='service-plans'>
            <CustomerServicePlans />
          </TabsContent>
          <SuperUserOnly>
            <TabsContent value='service-types'>
              <CustomerAdminServiceTypes />
            </TabsContent>
          </SuperUserOnly>
        </div>
      </div>
      <CustomerBooking />
    </div>
  );
}

function CustomerNameAndActions() {
  const { customer } = useCustomerCtx();

  const phoneFormatted = useMemo(() => {
    return formatPhoneNumber(customer.primary_phone || '');
  }, [customer.primary_phone]);

  return (
    <div className='flex w-full items-center gap-2'>
      <h1 className='text-heading-lg'>{customer.name}</h1>
      <div className='ml-auto flex items-center gap-2'>
        {customer.primary_email && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant='outline'
                size='icon'
                onClick={() =>
                  copyToClipboard({
                    text: customer.primary_email || '',
                    successMessage: `Email copied to clipboard`,
                  })
                }
              >
                <MailIcon className='size-4' />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Copy {customer.primary_email}</TooltipContent>
          </Tooltip>
        )}
        {customer.primary_phone && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant='outline'
                size='icon'
                onClick={() =>
                  copyToClipboard({
                    text: customer.primary_phone || '',
                    successMessage: `Phone number copied to clipboard`,
                  })
                }
              >
                <PhoneCallIcon className='size-4' />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Copy {phoneFormatted}</TooltipContent>
          </Tooltip>
        )}
      </div>
    </div>
  );
}
